import { createContext, useContext, useMemo, useState } from 'react';
import type { ModalData, StepperContextType, StepperProviderProps } from '../components/stepper/stepper.types';
const StepperContext = createContext<{
  getStepper: (id: string, totalSteps: number, initialStep?: number, onNext?: (fromStep: number, toStep: number) => void, onPrevious?: (fromStep: number, toStep: number) => void) => StepperContextType;
} | undefined>(undefined);
const getDefaultModalData = (totalSteps = 0, initialStep = 0, currentStep = 0): ModalData => ({
  initialStep,
  totalSteps,
  currentStep,
  isFirstStep: currentStep === 0,
  isLastStep: currentStep === totalSteps - 1
});
export const StepperProvider = ({
  children
}: StepperProviderProps) => {
  const [modalData, setModalData] = useState<Record<string, ModalData>>({});
  const contextValue = useMemo(() => {
    const getStepper = (id: string, totalSteps: number, initialStep = 0, onNext?: (fromStep: number, toStep: number) => void, onPrevious?: (fromStep: number, toStep: number) => void) => {
      const modal = modalData[id] ?? getDefaultModalData(totalSteps, initialStep, initialStep);
      const nextStep = () => {
        setModalData(prev => {
          const currentModal = prev[id] || modal;
          const newStep = currentModal.currentStep < totalSteps - 1 ? currentModal.currentStep + 1 : currentModal.currentStep;
          onNext?.(currentModal.currentStep, newStep);
          return {
            ...prev,
            [id]: {
              ...currentModal,
              currentStep: newStep
            }
          };
        });
      };
      const prevStep = () => {
        setModalData(prev => {
          const currentModal = prev[id] || modal;
          const newStep = currentModal.currentStep > 0 ? currentModal.currentStep - 1 : currentModal.currentStep;
          onPrevious?.(currentModal.currentStep, newStep);
          return {
            ...prev,
            [id]: {
              ...currentModal,
              currentStep: newStep
            }
          };
        });
      };
      const goToStep = (step: number) => {
        setModalData(prev => {
          const currentModal = prev[id] || modal;
          if (step >= 0 && step < currentModal.totalSteps) {
            return {
              ...prev,
              [id]: {
                ...currentModal,
                currentStep: step
              }
            };
          }
          return prev;
        });
      };
      const resetSteps = () => {
        setModalData(prev => ({
          ...prev,
          [id]: getDefaultModalData(prev[id]?.totalSteps || 0, 0, 0)
        }));
      };
      return {
        currentStep: modal.currentStep,
        nextStep,
        prevStep,
        goToStep,
        resetSteps,
        isFirstStep: modal.currentStep === 0,
        isLastStep: modal.currentStep === modal.totalSteps - 1
      };
    };
    return {
      getStepper
    };
  }, [modalData]);
  return <StepperContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="StepperProvider" data-sentry-source-file="use-stepper-modal.tsx">{children}</StepperContext.Provider>;
};

// Custom hook for consuming the stepper context
export function useStepper() {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error('useStepper must be used within a StepperProvider');
  }
  return context;
}